/**
 * 孩子端「语文听写」任务列表 —— `/student/dictation`。
 *
 * ## 用户原话（这个页面存在的全部理由）
 *
 * > "孩子端要有听写入口 —— 这样就不用大人陪着。"
 * > "孩子点「开始听写」→ 听 10 个 → 提交 → 想继续再点 → 再 10 个 → 再提交。"
 * > "孩子点「听写完了」只是这一轮先不听，任务还在，下次能继续。"
 *
 * ## 这一页只做三件事
 *
 * 1. 列出**家长布置给他的**批量任务与进度（还剩几个词）；
 * 2. 「开始听写」→ 让后端取下一批（`next-batch`）→ 进入播放器；
 * 3. 把三种"点不了"说成人话，而不是一个红色弹窗：
 *      · `DICTATION_ASSIGNMENT_INACTIVE` —— 家长暂停了这个任务；
 *      · `DICTATION_ASSIGNMENT_DONE` —— 全部听完了 🎉；
 *      · `DICTATION_ASSIGNMENT_NOT_FOUND` —— 家长删掉了它。
 *
 * ## 孩子在这里**看不到**的东西（刻意）
 *
 * 没有"结束任务"、没有"删除"、没有"改每次几个"：用户明确说过
 * "孩子不能结束整个任务，家长可以结束、暂停或删除整个任务"。
 * 后端也会拒（`canManageDictationAssignment` 对学生恒 false）——
 * 界面上不渲染按钮，是**不让他点了才发现失败**。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import { IconAlertTriangle, IconPlay } from '../../components/ui/Icons';
import { ApiError, toErrorMessage } from '../../lib/api/error';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import * as sessionApi from '../../lib/api/dictationSessions';
import { useMyStudent } from '../../lib/useMyStudent';
import { ERROR_CODES } from '@fht/shared';

const sKeys = {
  root: (studentId: string) => ['dictation-assignments', studentId] as const,
  list: (studentId: string) => ['dictation-assignments', studentId, 'list'] as const,
};

export function StudentDictationPage() {
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="我的听写">
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!my.studentId) {
    return (
      <AppShell kind="student" title="我的听写">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }

  return <StudentDictationScreen studentId={my.studentId} nickname={my.student?.nickname ?? ''} />;
}

function StudentDictationScreen({ studentId, nickname }: { studentId: string; nickname: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  /** 哪一个任务正在「取下一批」（只禁用那一个按钮，别把整页都锁住）。 */
  const [pendingId, setPendingId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: sKeys.list(studentId),
    queryFn: () => assignmentApi.listAssignments(studentId, { limit: 50 }),
    enabled: studentId !== '',
  });

  const assignments = listQuery.data?.assignments ?? null;
  const assignmentsMissing = listQuery.data !== undefined && assignments === null;

  const startMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentApi.startNextBatch(studentId, assignmentId),
    onMutate: (assignmentId: string) => setPendingId(assignmentId),
    onSuccess: async (result) => {
      setPendingId(null);
      await qc.invalidateQueries({ queryKey: sKeys.root(studentId) });
      navigate(`/student/dictation/${result.session.id}`);
    },
    onError: async (err: unknown) => {
      setPendingId(null);
      /**
       * 三种"拿不到新的一批"各有各的说法。⚠️ 一律**刷新列表**：
       * 这些错误都意味着"界面上的进度已经过时了"（家长刚暂停 / 刚被另一个窗口听完）。
       */
      if (err instanceof ApiError && err.is(ERROR_CODES.DICTATION_ASSIGNMENT_DONE)) {
        toast.showToast('这个任务的词已经全部听完啦 🎉 等家长布置新的任务吧', 'success');
      } else if (err instanceof ApiError && err.is(ERROR_CODES.DICTATION_ASSIGNMENT_INACTIVE)) {
        toast.showToast('家长暂时停下了这个听写任务，现在还不能开始', 'info');
      } else if (err instanceof ApiError && err.is(ERROR_CODES.DICTATION_ASSIGNMENT_NOT_FOUND)) {
        toast.showToast('这个任务已经被家长删掉了', 'info');
      } else {
        toast.showToast(`没能开始这一批：${toErrorMessage(err)}`, 'error');
      }
      await qc.invalidateQueries({ queryKey: sKeys.root(studentId) });
    },
  });

  const visible = (assignments ?? []).filter((item) => item.status !== 'finished');
  const finished = (assignments ?? []).filter((item) => item.status === 'finished');

  /**
   * **一次性的听写**（家长用「布置听写」直接出的那一次，`assignment_id` 为 NULL）。
   *
   * 2026-10-29 用户报："孩子端没有看到这项听写。" —— 原因正是这一页只列了
   * **批量任务**，而一次性听写不属于任何任务，孩子端此前根本没有入口。
   * 那一批词家长已经指定好了，孩子只需要点进来听（不需要"取下一批"）。
   *
   * 只列**还没交的**（pending / in_progress）：交过的属于历史，孩子不该再看到
   * "开始听写"（重听要家长在历史里点「再听一遍」）。
   */
  const oneOffQuery = useQuery({
    queryKey: ['dictation-sessions', studentId, 'one-off'] as const,
    queryFn: () => sessionApi.listSessions(studentId, { limit: 50 }),
    enabled: studentId !== '',
  });
  const oneOffPending = (oneOffQuery.data?.sessions ?? []).filter(
    (session) =>
      session.assignmentId === null &&
      (session.status === 'pending' || session.status === 'in_progress'),
  );

  return (
    <AppShell kind="student" title="我的听写">
      <div className="space-y-4">
        <Card className="bg-brand-50/60">
          <h2 className="text-base font-semibold text-slate-900">
            {nickname === '' ? '我的语文听写' : `${nickname} 的语文听写`}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            点「开始听写」听一批（每次几个由家长定好），写完点「提交」就行 —— 不用等大人陪。
            一批听一半去忙别的了也没关系，那些词下次还会出现。
          </p>
        </Card>

        {(listQuery.isError || assignmentsMissing) && (
          <ErrorState
            message={
              listQuery.isError
                ? `没拿到听写任务：${toErrorMessage(listQuery.error)}`
                : '没拿到听写任务（后端返回的字段名与预期不一致）—— 这不是"家长没有布置"。'
            }
            onRetry={() => void listQuery.refetch()}
            compact
          />
        )}

        <Card>
          <CardHeader
            title="现在能听的"
            description="进行中的排前面。暂停的任务要等家长恢复才能开始。"
          />

          {/* ---------- 一次性听写（家长直接布置的那一次） ---------- */}
          {/*
            ⚠️ 拉取失败要**说出来**（不静默）：孩子会以为"家长没布置"，
               而不是"这一块没取到"。批量任务那块有自己的报错条。
          */}
          {oneOffQuery.isError && (
            <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-rose-700">
              没拿到一次性听写：{toErrorMessage(oneOffQuery.error)}
              <Button size="sm" variant="secondary" onClick={() => void oneOffQuery.refetch()}>
                重试
              </Button>
            </p>
          )}
          {oneOffPending.length > 0 && (
            <ul className="mb-3 space-y-2">
              {oneOffPending.map((session) => (
                <li
                  key={session.id}
                  className="rounded-2xl border border-brand-200 bg-brand-50/40 px-4 py-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold text-slate-900">
                        {session.title}
                      </p>
                      <p className="mt-0.5 text-sm text-slate-500">
                        {session.questionCount} 个词 · 家长已经选好这一次的词
                      </p>
                    </div>
                    <Badge tone="brand">{session.status === 'in_progress' ? '继续听' : '待开始'}</Badge>
                  </div>
                  <div className="mt-3">
                    <Button
                      size="lg"
                      icon={<IconPlay size={20} />}
                      onClick={() => navigate(`/student/dictation/${session.id}`)}
                    >
                      {session.status === 'in_progress' ? '继续听写' : '开始听写'}
                      {session.questionCount > 0 ? `（${session.questionCount} 个）` : ''}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {listQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : visible.length === 0 ? (
            oneOffPending.length > 0 ? null : (
              <EmptyState
                icon="✍️"
                title="现在没有要听的听写"
                description="家长布置了听写（批量任务或一次性听写）之后，这里就会出现「开始听写」的按钮。"
              />
            )
          ) : (
            <ul className="space-y-3">
              {visible.map((assignment) => {
                const percent = assignmentApi.progressPercent(assignment.progress);
                const done = assignment.progress.allDone === true;
                const paused = assignment.status === 'paused';
                const remaining = assignment.progress.remainingWords;
                return (
                  <li key={assignment.id} className="rounded-2xl border border-slate-200 px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-semibold text-slate-900">
                          {assignment.title}
                        </p>
                        <p className="mt-0.5 text-sm text-slate-500">
                          每次 {assignment.batchSize} 个 ·{' '}
                          {assignmentApi.describeOrder(assignment.shuffle)}
                        </p>
                      </div>
                      <Badge tone={paused ? 'warn' : done ? 'success' : 'brand'}>
                        {done ? '已听完' : paused ? '已暂停' : '进行中'}
                      </Badge>
                    </div>

                    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${percent}%` }}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                        aria-label="听写进度"
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {assignmentApi.describeProgress(assignment.progress)}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {done ? (
                        <span className="text-sm font-medium text-emerald-700">
                          全部听完了，太棒了 🎉
                        </span>
                      ) : (
                        <Button
                          size="lg"
                          icon={<IconPlay size={20} />}
                          disabled={paused}
                          loading={pendingId === assignment.id && startMutation.isPending}
                          onClick={() => startMutation.mutate(assignment.id)}
                        >
                          开始听写
                          {typeof remaining === 'number' && remaining > 0
                            ? `（这一批 ${Math.min(assignment.batchSize, remaining)} 个）`
                            : ''}
                        </Button>
                      )}
                      {paused && (
                        <span className="text-sm text-amber-700">家长暂停了这个任务，先等等。</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {finished.length > 0 && (
          <Card className="bg-slate-50">
            <CardHeader title="已经结束的任务" description="不会再出新的一批，但听过的记录都还在。" />
            <ul className="space-y-1.5">
              {finished.map((assignment) => (
                <li key={assignment.id} className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">{assignment.title}</span>
                  <span className="ml-2 text-xs text-slate-400">
                    {assignmentApi.describeProgress(assignment.progress)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="bg-slate-50">
          <p className="flex items-start gap-2 text-sm text-slate-600">
            <span className="mt-0.5 shrink-0 text-slate-400">
              <IconAlertTriangle size={16} />
            </span>
            <span>
              听不清楚的时候可以用「重播这一题」；整批听完了记得点「提交」——
              提交之后这一批才算听过（家长也能看到你的进度）。
            </span>
          </p>
        </Card>
      </div>
    </AppShell>
  );
}
